import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../services/api";
import { isPermite } from "../../utilities/isPermite";
import {
    ChatContato,
    ChatConversa,
    ChatMensagemType,
    CHAT_PERM_USAR,
    POLL_ABERTO,
    POLL_FECHADO,
} from "./chatTypes";

type ChatContextType = {
    habilitado: boolean
    aberto: boolean
    idUsuarioLogado: number
    conversas: ChatConversa[]
    contatos: ChatContato[]
    totalNaoLidas: number
    conversaAtivaId: number | null
    conversaAtiva: ChatConversa | null
    mensagens: ChatMensagemType[]
    carregandoMensagens: boolean
    temMais: boolean
    enviando: boolean
    abrirChat: () => void
    fecharChat: () => void
    alternarChat: () => void
    abrirConversa: (id: number | null) => Promise<void>
    abrirConversaComUsuario: (idUsuario: number) => Promise<void>
    criarGrupo: (nome: string, participantes: number[]) => Promise<boolean>
    atualizarGrupo: (id: number, nome: string, participantes: number[]) => Promise<boolean>
    sairDaConversa: (id: number) => Promise<boolean>
    enviarMensagem: (texto: string, mencoes?: number[]) => Promise<void>
    enviarAnexos: (arquivos: File[], texto: string, mencoes?: number[]) => Promise<void>
    apagarMensagem: (id: number) => Promise<void>
    carregarMaisMensagens: () => Promise<void>
    recarregarConversas: () => Promise<void>
};

const ChatContext = createContext<ChatContextType | null>(null);

export function useChat() {
    const ctx = useContext(ChatContext);
    if (!ctx) throw new Error("useChat precisa estar dentro de <ChatProvider>");
    return ctx;
}

const LIMITE_PAGINA = 30;

function lerSessao() {
    try {
        return JSON.parse(String(sessionStorage.getItem('infoLogin'))) || {};
    } catch {
        return {};
    }
}

export default function ChatProvider({ children }: { children: React.ReactNode }) {
    // O Provider fica ACIMA das rotas para o chat sobreviver à navegação; como o
    // login troca de rota sem recarregar a página, a sessão é relida a cada rota.
    const location = useLocation();
    const [infoLogin, setInfoLogin] = useState<any>(lerSessao);

    useEffect(() => {
        const atual = lerSessao();
        setInfoLogin((prev: any) => (prev?.token === atual?.token ? prev : atual));
    }, [location.pathname]);

    const idUsuarioLogado = Number(infoLogin?.id_usuario || 0);
    const habilitado = useMemo(() => Boolean(infoLogin?.token) && isPermite(CHAT_PERM_USAR), [infoLogin]);

    const [aberto, setAberto] = useState(false);
    const [conversas, setConversas] = useState<ChatConversa[]>([]);
    const [contatos, setContatos] = useState<ChatContato[]>([]);
    const [totalNaoLidas, setTotalNaoLidas] = useState(0);
    const [conversaAtivaId, setConversaAtivaId] = useState<number | null>(null);
    const [mensagens, setMensagens] = useState<ChatMensagemType[]>([]);
    const [carregandoMensagens, setCarregandoMensagens] = useState(false);
    const [temMais, setTemMais] = useState(false);
    const [enviando, setEnviando] = useState(false);

    // refs evitam recriar o polling a cada render (o timer não pode reiniciar a cada sync)
    const ultimoIdRef = useRef(0);
    const conversaAtivaRef = useRef<number | null>(null);
    const conversasRef = useRef<ChatConversa[]>([]);
    const sincronizandoRef = useRef(false);

    useEffect(() => { conversaAtivaRef.current = conversaAtivaId; }, [conversaAtivaId]);
    useEffect(() => { conversasRef.current = conversas; }, [conversas]);

    // Logout: zera tudo e para o polling
    useEffect(() => {
        if (habilitado) return;
        setConversas([]);
        setContatos([]);
        setMensagens([]);
        setTotalNaoLidas(0);
        setConversaAtivaId(null);
        setAberto(false);
        ultimoIdRef.current = 0;
    }, [habilitado]);

    // ── Carregamentos ─────────────────────────────────────────────────────────

    const recarregarConversas = useCallback(async () => {
        try {
            const response = await api.get(`/chatconversas`);
            if (Array.isArray(response.data)) {
                setConversas(response.data);
                setTotalNaoLidas(response.data.reduce((acc: number, c: ChatConversa) => acc + Number(c.nao_lidas || 0), 0));
            }
        } catch (error) {
            console.log(error);
        }
    }, []);

    const carregarContatos = useCallback(async () => {
        try {
            const response = await api.get(`/chatcontatos`);
            if (Array.isArray(response.data)) setContatos(response.data);
        } catch (error) {
            console.log(error);
        }
    }, []);

    const marcarLida = useCallback(async (id_chat_conversa: number, id_ultima_mensagem_lida: number) => {
        const conversa = conversasRef.current.find(c => c.id_chat_conversa === id_chat_conversa);
        const zeradas = Number(conversa?.nao_lidas || 0);
        setConversas(prev => prev.map(c => c.id_chat_conversa === id_chat_conversa ? { ...c, nao_lidas: 0 } : c));
        if (zeradas > 0) setTotalNaoLidas(prev => Math.max(0, prev - zeradas));
        try {
            await api.post(`/readchatconversa/${id_chat_conversa}`, { id_ultima_mensagem_lida });
        } catch (error) {
            console.log(error);
        }
    }, []);

    const abrirConversa = useCallback(async (id: number | null) => {
        setConversaAtivaId(id);
        setMensagens([]);
        setTemMais(false);
        if (!id) return;

        setCarregandoMensagens(true);
        try {
            const response = await api.get(`/chatmensagens/${id}?limit=${LIMITE_PAGINA}`);
            if (response.data?.error) return;

            const lista: ChatMensagemType[] = response.data.mensagens || [];
            setMensagens(lista);
            setTemMais(Boolean(response.data.tem_mais));

            if (lista.length > 0) {
                const ultima = lista[lista.length - 1].id_chat_mensagem;
                if (ultima > ultimoIdRef.current) ultimoIdRef.current = ultima;
                await marcarLida(id, ultima);
            } else {
                await marcarLida(id, 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setCarregandoMensagens(false);
        }
    }, [marcarLida]);

    const carregarMaisMensagens = useCallback(async () => {
        const id = conversaAtivaRef.current;
        if (!id || mensagens.length === 0 || !temMais) return;

        setCarregandoMensagens(true);
        try {
            const before = mensagens[0].id_chat_mensagem;
            const response = await api.get(`/chatmensagens/${id}?limit=${LIMITE_PAGINA}&before=${before}`);
            if (response.data?.error) return;

            const anteriores: ChatMensagemType[] = response.data.mensagens || [];
            setMensagens(prev => [...anteriores, ...prev]);
            setTemMais(Boolean(response.data.tem_mais));
        } catch (error) {
            console.log(error);
        } finally {
            setCarregandoMensagens(false);
        }
    }, [mensagens, temMais]);

    // ── Polling adaptativo ────────────────────────────────────────────────────

    const sincronizar = useCallback(async () => {
        if (sincronizandoRef.current) return;
        sincronizandoRef.current = true;
        try {
            const response = await api.get(`/chatpoll?since=${ultimoIdRef.current}`);
            if (response.data?.error) return;

            const { total_nao_lidas = 0, conversas: resumo = [], novas = [], ultimo_id = 0 } = response.data;
            setTotalNaoLidas(Number(total_nao_lidas));
            if (Number(ultimo_id) > ultimoIdRef.current) ultimoIdRef.current = Number(ultimo_id);

            // conversa nova (alguém abriu DM comigo) => recarrega a lista completa
            if (resumo.length !== conversasRef.current.length) {
                await recarregarConversas();
            } else {
                setConversas(prev => prev.map(c => {
                    const r = resumo.find((x: any) => x.id_chat_conversa === c.id_chat_conversa);
                    if (!r) return c;
                    return {
                        ...c,
                        nao_lidas: Number(r.nao_lidas || 0),
                        ultima_mensagem_em: r.ultima_mensagem_em,
                        ultima_mensagem_texto: r.ultima_mensagem_texto,
                    };
                }).sort((a, b) => String(b.ultima_mensagem_em || '').localeCompare(String(a.ultima_mensagem_em || ''))));
            }

            // mensagens novas da conversa aberta entram na tela na hora
            const ativa = conversaAtivaRef.current;
            if (ativa && novas.length > 0) {
                const daAtiva: ChatMensagemType[] = novas.filter((m: ChatMensagemType) => m.id_chat_conversa === ativa);
                if (daAtiva.length > 0) {
                    setMensagens(prev => {
                        const conhecidos = new Set(prev.map(m => m.id_chat_mensagem));
                        return [...prev, ...daAtiva.filter(m => !conhecidos.has(m.id_chat_mensagem))];
                    });
                    await marcarLida(ativa, daAtiva[daAtiva.length - 1].id_chat_mensagem);
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            sincronizandoRef.current = false;
        }
    }, [marcarLida, recarregarConversas]);

    // Carga inicial
    useEffect(() => {
        if (!habilitado) return;
        recarregarConversas();
        carregarContatos();
        sincronizar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [habilitado]);

    // Um único timer: 4s com o chat aberto, 20s fechado, pausado com a aba oculta
    useEffect(() => {
        if (!habilitado) return;

        const tick = () => {
            if (document.visibilityState === 'hidden') return;
            sincronizar();
        };
        const intervalo = setInterval(tick, aberto ? POLL_ABERTO : POLL_FECHADO);

        const aoVoltar = () => {
            if (document.visibilityState === 'visible') sincronizar();
        };
        document.addEventListener('visibilitychange', aoVoltar);

        return () => {
            clearInterval(intervalo);
            document.removeEventListener('visibilitychange', aoVoltar);
        };
    }, [habilitado, aberto, sincronizar]);

    // ── Ações ─────────────────────────────────────────────────────────────────

    const abrirChat = useCallback(() => setAberto(true), []);
    const fecharChat = useCallback(() => setAberto(false), []);
    const alternarChat = useCallback(() => setAberto(prev => !prev), []);

    const abrirConversaComUsuario = useCallback(async (idUsuario: number) => {
        try {
            const response = await api.post(`/createchatconversa`, {
                tipo_conversa: 'direta',
                participantes_ids: [idUsuario],
            });
            if (response.data?.error) return;
            await recarregarConversas();
            await abrirConversa(Number(response.data.id_chat_conversa));
            setAberto(true);
        } catch (error) {
            console.log(error);
        }
    }, [abrirConversa, recarregarConversas]);

    const criarGrupo = useCallback(async (nome: string, participantes: number[]) => {
        try {
            const response = await api.post(`/createchatconversa`, {
                tipo_conversa: 'grupo',
                nome_conversa: nome,
                participantes_ids: participantes,
            });
            if (response.data?.error) return false;
            await recarregarConversas();
            await abrirConversa(Number(response.data.id_chat_conversa));
            return true;
        } catch (error) {
            console.log(error);
            return false;
        }
    }, [abrirConversa, recarregarConversas]);

    const atualizarGrupo = useCallback(async (id: number, nome: string, participantes: number[]) => {
        try {
            const response = await api.put(`/updatechatconversa/${id}`, {
                nome_conversa: nome,
                participantes_ids: participantes,
            });
            if (response.data?.error) return false;
            await recarregarConversas();
            return true;
        } catch (error) {
            console.log(error);
            return false;
        }
    }, [recarregarConversas]);

    const sairDaConversa = useCallback(async (id: number) => {
        try {
            const response = await api.delete(`/deletechatconversa/${id}`);
            if (response.data?.error) return false;
            if (conversaAtivaRef.current === id) {
                setConversaAtivaId(null);
                setMensagens([]);
            }
            await recarregarConversas();
            return true;
        } catch (error) {
            console.log(error);
            return false;
        }
    }, [recarregarConversas]);

    const enviarMensagem = useCallback(async (texto: string, mencoes: number[] = []) => {
        const id = conversaAtivaRef.current;
        if (!id || String(texto).trim() === '') return;

        setEnviando(true);
        try {
            const response = await api.post(`/sendchatmensagem`, {
                id_chat_conversa: id,
                conteudo_mensagem: texto,
                mencoes,
            });
            if (response.data?.error) return;
            await sincronizar();
        } catch (error) {
            console.log(error);
        } finally {
            setEnviando(false);
        }
    }, [sincronizar]);

    const enviarAnexos = useCallback(async (arquivos: File[], texto: string, mencoes: number[] = []) => {
        const id = conversaAtivaRef.current;
        if (!id || arquivos.length === 0) return;

        setEnviando(true);
        try {
            const form = new FormData();
            form.append('id_chat_conversa', String(id));
            form.append('conteudo_mensagem', texto || '');
            // multipart nao carrega array: vai como "12,45" e o backend valida os ids
            form.append('mencoes', mencoes.join(','));
            arquivos.forEach(f => form.append('files', f));

            // sem Content-Type manual: o browser precisa gerar o boundary do multipart
            const response = await api.post(`/sendchatanexo`, form);
            if (response.data?.error) return;
            await sincronizar();
        } catch (error) {
            console.log(error);
        } finally {
            setEnviando(false);
        }
    }, [sincronizar]);

    const apagarMensagem = useCallback(async (id: number) => {
        try {
            const response = await api.delete(`/deletechatmensagem/${id}`);
            if (response.data?.error) return;
            setMensagens(prev => prev.map(m => m.id_chat_mensagem === id
                ? { ...m, chat_mensagem_ativa: 0, conteudo_mensagem: null, anexos: [] }
                : m));
        } catch (error) {
            console.log(error);
        }
    }, []);

    const conversaAtiva = useMemo(
        () => conversas.find(c => c.id_chat_conversa === conversaAtivaId) || null,
        [conversas, conversaAtivaId]
    );

    const value: ChatContextType = {
        habilitado,
        aberto,
        idUsuarioLogado,
        conversas,
        contatos,
        totalNaoLidas,
        conversaAtivaId,
        conversaAtiva,
        mensagens,
        carregandoMensagens,
        temMais,
        enviando,
        abrirChat,
        fecharChat,
        alternarChat,
        abrirConversa,
        abrirConversaComUsuario,
        criarGrupo,
        atualizarGrupo,
        sairDaConversa,
        enviarMensagem,
        enviarAnexos,
        apagarMensagem,
        carregarMaisMensagens,
        recarregarConversas,
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
